<script>
	import { onMount } from 'svelte';
	
	// 각 섹션에 대한 참조를 저장할 객체
	let sections = {
		aboutMe: null,
		aboutAidan: null,
		whyICode: null,
		myVision: null
	};
	
	// 활성화된 섹션을 추적하는 변수
	let activeSection = 'aboutMe';
	
	// 스크롤 이벤트 처리 함수
	function handleScroll() {
		const scrollPosition = window.scrollY + 100; // 약간의 오프셋 추가
		
		// 각 섹션의 위치를 확인하고 활성화된 섹션 업데이트
		for (const [id, element] of Object.entries(sections)) {
			if (element && scrollPosition >= element.offsetTop) {
				activeSection = id;
			}
		}
	}
	
	// 특정 섹션으로 스크롤하는 함수
	function scrollToSection(sectionId) {
		const element = sections[sectionId];
		if (element) {
			window.scrollTo({
				top: element.offsetTop - 80, // 헤더 높이만큼 오프셋 적용
				behavior: 'smooth'
			});
			activeSection = sectionId;
		}
	}
	
	onMount(() => {
		// 스크롤 이벤트 리스너 등록
		window.addEventListener('scroll', handleScroll);
		
		// 컴포넌트 언마운트 시 이벤트 리스너 제거
		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	});
</script>

<div class="about-container">
	<div class="table-of-contents">
		<div class="toc-header">Contents</div>
		<div class="toc-items">
			<button 
				class:active={activeSection === 'aboutMe'} 
				on:click={() => scrollToSection('aboutMe')}>
				I'm Aiden
			</button>
			<button 
				class:active={activeSection === 'aboutAidan'} 
				on:click={() => scrollToSection('aboutAidan')}>
				About Aidan
			</button>
			<button 
				class:active={activeSection === 'whyICode'} 
				on:click={() => scrollToSection('whyICode')}>
				Why I Code
			</button>
			<button 
				class:active={activeSection === 'myVision'} 
				on:click={() => scrollToSection('myVision')}>
				My Vision
			</button>
		</div>
	</div>
	
	<div class="about-content">
		<h1>About Me</h1>
		
		<section bind:this={sections.aboutMe}>
			<h2 id="about-me">I'm Aiden (Kyungmin Park)</h2>
			<p>Hi, I'm Aiden — a 2005-born aspiring front-end developer.<br><br>
				I'm currently studying Software Engineering, and beyond my university coursework, I've built several websites and apps on my own, just out of passion and curiosity.<br><br>
				I speak English and a bit of Japanese too.<br><br>
				I'm inspired by people who grow through their own drive.<br>
				To me, the best kind of growth is shared — where not only I, but also the people around me, keep learning and moving forward together.<br><br>
			</p>
		</section>
		
		<section bind:this={sections.aboutAidan}>
			<h2 id="about-aidan">About Aidan</h2>
			<p><strong>Since 2024,</strong> I've been designing and developing a variety of websites and Android apps — from small personal projects to freelance work for clients. These experiences have helped me understand the real-world needs of users and how to build clean, functional interfaces that meet them.<br><br>
				<strong>In 2025,</strong>I started studying Software Engineering at Korea University, where I'm diving deeper into computer science fundamentals while continuing to build side projects and refine my development skills.<br><br></p>
		</section>

		<section bind:this={sections.whyICode}>
			<h2 id="why-i-code">Why I Code</h2>
			<p>My interest in coding started early — I was fascinated by how websites and apps were made, so I began teaching myself using online resources and tinkering with small projects. I still remember the thrill of seeing my first "Hello World" on the screen.<br><br>
				What began as a hobby quickly grew into a real passion. I love breaking down complex problems, building things from scratch, and seeing ideas come to life through code. It’s this creative problem-solving aspect that keeps me excited every day.<br><br></p>
		</section>

		<section bind:this={sections.myVision}>
			<h2 id="my-vision">My Vision</h2>
			<p>I'm working towards becoming a well-rounded full-stack developer — someone who not only understands both the front-end and back-end, but also knows how to collaborate effectively, write maintainable code, and think like a product designer when needed.<br><br>
				In the future, I hope to be part of a team where I can build meaningful products that people actually use and love — while also helping those around me grow and succeed. To me, that’s what great development is all about.<br><br><br><br></p>
		</section>
	</div>
</div>

<style>
	.about-container {
		display: flex;
		max-width: 1200px;
		margin: 80px auto 0;
		padding: 0 20px;
		gap: 40px;
	}
	
	.table-of-contents {
		position: sticky;
		top: 80px;
		height: fit-content;
		min-width: 200px;
		border-right: 1px solid #eee;
		padding-right: 20px;
	}
	
	.toc-header {
		font-weight: bold;
		font-size: 1.2rem;
		margin-bottom: 15px;
		color: var(--color-text, #000000);
	}
	
	.toc-items {
		display: flex;
		flex-direction: column;
		gap: 10px;
	}
	
	.toc-items button {
		background: none;
		border: none;
		text-align: left;
		padding: 5px 10px;
		border-radius: 4px;
		cursor: pointer;
		font-size: 0.9rem;
		color: #666;
		transition: all 0.2s ease;
	}
	
	.toc-items button:hover {
		background-color: none;
		color: var(--color-text, #000000);
	}
	
	.toc-items button.active {
		background-color: none;
		color: var(--color-text, #000000);
		font-weight: 600;
	}

	.about-content {
		flex: 1;
		max-width: 800px;
	}

	h1 {
		font-size: var(--font-size-title, 2rem);
		margin-bottom: 1.5rem;
		color: var(--color-text, #000000);
	}

	h2 {
		margin: 0 0 1rem 0;
		font-size: 1.5rem;
		border-bottom: 1px solid #eee;
		padding-bottom: 0.5rem;
		scroll-margin-top: 100px; /* 스크롤 시 여백 확보 */
	}

	p {
		margin: 0 0 1rem 0;
		font-size: 1rem;
		color: #b6b6b6;
	}

	section {
		margin-bottom: 2rem;
		line-height: 1.6;
	}
	
	/* 반응형 디자인 */
	@media (max-width: 768px) {
		.about-container {
			flex-direction: column;
		}
		
		.table-of-contents {
			position: relative;
			top: 0;
			width: 100%;
			border-right: none;
			border-bottom: 1px solid #eee;
			padding-bottom: 15px;
			margin-bottom: 20px;
		}
		
		.toc-items {
			flex-direction: row;
			flex-wrap: wrap;
		}
		
		.toc-items button {
			font-size: 0.8rem;
		}
	}
</style>